// packages/contracts/src/settlement-dispatch.ts

/**
 * Whether anything a caller does on this gateway can reach a Payment Agency.
 *
 * ⚠ A CAPABILITY, not a deployment name — the same distinction
 * {@link IdentityProvider} exists for, applied to the second half of the same
 * sentence.
 *
 * ## What this is here to answer
 *
 * `apps/officer-web` shows a synthetic-data notice that asserts, as flat fact:
 * *"no real Authority data is used, and nothing you do here reaches a live
 * payment agency"*. That is two claims, and S-016 is what happens when a client
 * decides them from a deployment name: the notice keys on `environment`, which
 * special-cases the exact string `production` and lets `PRODUCTION`, `prod`,
 * `live` or a typo through to the reassuring branch. An allow-list question
 * answered permissively, about safety, in the dangerous direction.
 *
 * The second claim is a fact about **what this gateway installed**, which the
 * server knows exactly and the client cannot know at all:
 *
 * - `none` — no Payment Agency interface is wired into this gateway. Nothing a
 *   caller does here produces a settlement instruction that leaves this system.
 *   This is the state today, and it is not an assumption: it is asserted at the
 *   point in `buildServer` where such an adapter would be constructed, and
 *   `services/gateway/src/settlement-dispatch.test.ts` fails if any outbound
 *   call appears in the server packages while it still says `none`.
 * - `simulated` — an interface is installed, and it points at a test double or a
 *   sandbox. Files are produced; no real payment is ever instructed. Not built.
 * - `live` — an interface to a real Payment Agency is installed. A client MUST
 *   NOT claim that nothing here reaches one. Not built.
 *
 * `simulated` and `live` are declared before they exist for the reason
 * `federated` is: the client's handling of a value is written, reviewed and
 * typed BEFORE the day it starts arriving, rather than first met in production.
 *
 * ## What this deliberately does NOT claim
 *
 * ⚠ **It says nothing about whether the DATA is synthetic.** That is the other
 * half of the notice's sentence, and this gateway cannot honestly assert it: it
 * connects to whatever `DATABASE_URL` names, and a marker left behind by the
 * synthetic seed would still be there after real data was loaded alongside it —
 * a safety claim that survives the world changing underneath it, which is the
 * exact defect class of S-016 rather than a fix for it. A client must therefore
 * attribute that half or drop it; see S-016 in `docs/security/SECURITY.md` for
 * the contract agreed with the UI.
 *
 * ⚠ PRS does not pay anyone in any deployment (P2C1 §1.1 — settlement files are
 * produced, and the payment from a Payment Agency to a Healthcare Service
 * Provider "occurs outside of PRS scope"). `live` therefore means *"instructions
 * this gateway emits are acted on by a real Payment Agency"*, not *"this gateway
 * moves money"*. The distinction matters for the copy a client writes.
 */
export type SettlementDispatch = 'none' | 'simulated' | 'live'

/** Every value {@link SettlementDispatch} admits, for narrowing at a boundary. */
export const SETTLEMENT_DISPATCHES: readonly SettlementDispatch[] = ['none', 'simulated', 'live']

/**
 * Narrow an unknown value — a parsed JSON body, typically — to a
 * {@link SettlementDispatch}.
 *
 * Returns `false` for anything unrecognised rather than defaulting to one.
 * Defaulting here would pick between "tell the user nothing they do can reach a
 * payment agency" and "say nothing", and `none` is the dangerous default of the
 * two: it is the one that licenses a reassurance.
 */
export function isSettlementDispatch(value: unknown): value is SettlementDispatch {
  return typeof value === 'string' && (SETTLEMENT_DISPATCHES as readonly string[]).includes(value)
}

/**
 * What ONE response actually said about `settlementDispatch`.
 *
 * ⚠ Identical in shape to {@link IdentityProviderReport}, and for the reason
 * S-017 records rather than for symmetry: a field the server did not send
 * arrives as `undefined` however confidently a client's `interface` declares it
 * a `string`, because a JSON body reaches that interface by a cast. An older
 * gateway behind a newer bundle, a proxy that strips the field, or a body
 * carrying `''` must not be able to produce the reassuring branch. The EXISTENCE
 * of a report is the fact that a response arrived; `reported: null` is "it
 * answered and did not say", which is not evidence of safety.
 */
export interface SettlementDispatchReport {
  readonly reported: string | null
}

/**
 * Read a response's `settlementDispatch` into a {@link SettlementDispatchReport}.
 *
 * ⚠ No trimming, no case folding, no normalisation — `'  '` is a value the
 * server sent, matches nothing, and must surface as unrecognised rather than be
 * tidied into silence. Only a genuinely empty string is "said nothing".
 */
export function readSettlementDispatch(value: unknown): SettlementDispatchReport {
  return { reported: typeof value === 'string' && value.length > 0 ? value : null }
}
